import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiEnvelope, ApiResult, classifyHttpError, unwrapEnvelope } from '../customer/api-result';
import { FulfilmentConfig } from './fulfilment-dtos';

const CACHE_PREFIX = 'tc.fulfil.';
/** Short TTL — config is read once per checkout entry; long enough to avoid
 *  re-fetching across the delivery flow's screens, short enough that an admin
 *  toggling delivery is reflected on the next visit. */
const CACHE_TTL_MS = 5 * 60 * 1000;

/**
 * D4A — fulfilment config client. Fetches once per public token, caches in
 * sessionStorage with a short TTL, and exposes the active config as signals
 * so the mode selector + delivery checkout read one source of truth.
 *
 * Dark-by-default: until a config is loaded, `deliveryEnabled` is false, so no
 * delivery affordance ever renders speculatively.
 */
@Injectable({ providedIn: 'root' })
export class FulfilmentService {
  private http = inject(HttpClient);
  private baseUrl = environment.apiUrl;

  private readonly _config = signal<FulfilmentConfig | null>(null);
  private readonly _boundToken = signal<string | null>(null);

  readonly config = this._config.asReadonly();
  readonly deliveryEnabled = computed(() => this._config()?.deliveryOrderingEnabled === true);
  readonly pickupEnabled = computed(() => this._config()?.pickupEnabled === true);
  /** True only for pickup QR tokens — dine-in table tokens never surface the
   *  Pickup/Delivery selector. */
  readonly isPickupToken = computed(() => this._config()?.isPickup === true);
  /**
   * OMNI-2a — F3: the tenant's channel availability flag, from the same config.
   * False until the config loads, and false when the field is absent. While it
   * is false the guest menu reads never carry a fulfilment, so their URLs and
   * cache keys stay exactly as they were before OMNI-2a.
   */
  readonly channelAvailabilityEnabled = computed(() => this._config()?.channelAvailabilityEnabled === true);
  // ── DADR — the store's delivery address form shape ──
  // Absent (a store whose settings predate DADR, or a config served from an
  // older cache entry) means ON, matching the DB default — so the form does not
  // silently vanish for a store that never opted out.
  readonly addressDefaultStructureEnabled = computed(
    () => this._config()?.deliveryAddressDefaultStructureEnabled !== false);
  readonly addressMapPinEnabled = computed(
    () => this._config()?.deliveryAddressMapPinEnabled !== false);
  readonly addressFields = computed(
    () => this._config()?.deliveryAddressFields ?? []);
  readonly codEnabled = computed(() => this._config()?.codEnabled === true);
  /** CANX-4 — store accepts customer cancellation requests on unaccepted
   *  delivery orders. Gated on deliveryEnabled like every delivery field. */
  readonly cancellationRequestsEnabled = computed(() =>
    this.deliveryEnabled() && this._config()?.deliveryCancellationRequestsEnabled === true);
  readonly onlinePaymentEnabled = computed(() =>
    this.deliveryEnabled() && this._config()?.deliveryOnlinePaymentEnabled === true);
  readonly feeFlat = computed(() => this.deliveryEnabled() ? (this._config()?.deliveryFeeFlat ?? 0) : 0);
  readonly minimumOrder = computed(() => this.deliveryEnabled() ? (this._config()?.minimumDeliveryOrderAmount ?? 0) : 0);
  readonly hoursJson = computed(() => this.deliveryEnabled() ? (this._config()?.deliveryHoursJson ?? null) : null);
  readonly etaMinutes = computed(() => this.deliveryEnabled() ? (this._config()?.deliveryEtaMinutes ?? null) : null);
  readonly mapsApiKey = computed(() => this.deliveryEnabled() ? (this._config()?.googleMapsApiKey ?? null) : null);
  readonly currencyCode = computed(() => this._config()?.currencyCode ?? '');
  readonly storeName = computed(() => this._config()?.storeName ?? '');

  /**
   * Resolve config for a token. Serves a fresh sessionStorage cache hit
   * synchronously (still as an Observable) and otherwise fetches. Always
   * publishes into the signals so consumers reading the signal see the result.
   */
  getConfig(publicToken: string): Observable<ApiResult<FulfilmentConfig>> {
    if (this._boundToken() !== publicToken) {
      this._boundToken.set(publicToken);
      this._config.set(null);
    }
    const cached = this.readCache(publicToken);
    if (cached) {
      this._config.set(cached);
      return of({ ok: true, data: cached } as ApiResult<FulfilmentConfig>);
    }
    const url = `${this.baseUrl}/public/store/${encodeURIComponent(publicToken)}/fulfilment-config`;
    return this.http.get<ApiEnvelope<FulfilmentConfig>>(url).pipe(
      map(res => unwrapEnvelope(res)),
      tap(res => {
        if (res.ok) {
          this._config.set(res.data);
          this.writeCache(publicToken, res.data);
        }
      }),
      catchError((err: HttpErrorResponse) => of(classifyHttpError<FulfilmentConfig>(err))),
    );
  }

  private cacheKey(token: string): string { return `${CACHE_PREFIX}${token}`; }

  private readCache(token: string): FulfilmentConfig | null {
    try {
      const raw = sessionStorage.getItem(this.cacheKey(token));
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { ts: number; data: FulfilmentConfig } | null;
      if (!parsed || typeof parsed.ts !== 'number') return null;
      if (Date.now() - parsed.ts > CACHE_TTL_MS) { sessionStorage.removeItem(this.cacheKey(token)); return null; }
      return parsed.data;
    } catch { return null; }
  }

  private writeCache(token: string, data: FulfilmentConfig): void {
    try { sessionStorage.setItem(this.cacheKey(token), JSON.stringify({ ts: Date.now(), data })); }
    catch { /* private mode / quota — ignore */ }
  }
}
